import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import { ErrorCode } from '@ancon/wildcat-types'
import noop from 'lodash/noop'

import Button from '../Button'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import { appErrorModalConfigSelector } from '../../store/appSelectors'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { appSetIsErrorModalVisible } from '../../store/appSlice'
import BodyText from '../BodyText'
import { ErrorModalType, InformativeError } from '../../types'
import { setProductDetailsModalVisible } from '../../../product/store/productSlice'
import storedPaymentCorrelationId from '../../utils/storedPaymentCorrelationId'
import {
  checkoutRetryAdyenPayment,
  checkoutSetIsVisibleBoxPickupScheduleOrderModal,
} from '../../../checkout/store/checkoutSlice'
import { checkRetryAdyenPayment } from '../../../check/store/checkSlice'
import { outletSetOrderConfigureFilterModal } from '../../../outlet/store/outletSlice'
import { OrderConfigureFilterMode } from '../../../outlet/types'
import { setPreOrderTimeChangeModalVisible } from '../../../preOrder/store/preOrderSlice'
import { deleteCheckoutItems } from '../../../checkout/store/checkoutThunks'
import { checkoutItemsSelector } from '../../../checkout/store/checkoutSelectors'
import localStorageUtils from '../../utils/localStorageUtils'
import { clientContextSetCurrentCompanyUserId } from '../../../clientContext/store/clientContextSlice'
import { setAuthProfileSelectionModalType } from '../../../auth/store/authSlice'
import { ProfileSelectionModalType } from '../../../auth/types'
import { setPendingCompanyProfileSwitchMemberId } from '../../../company/store/companySlice'
import { companyPendingProfileSwitchMemberIdSelector } from '../../../company/store/companySelectors'
import { clientContextCompanyIdByCompanyMemberIdSelector } from '../../../clientContext/store/clientContextSelectors'
import useAppStore from '../../../../store/hooks/useAppStore'

import styles from './ErrorModalFooter.module.scss'

export default function ErrorModalFooter() {
  const store = useAppStore()

  const pendingMemberId = useAppSelector(
    companyPendingProfileSwitchMemberIdSelector,
  )
  const dispatch = useAppDispatch()
  const { t } = useTranslation('common')

  const errorModalConfig = useAppSelector(appErrorModalConfigSelector)
  const checkoutItems = useAppSelector(checkoutItemsSelector)

  function getProductVariantIdByItemIds(
    checkoutItemProductVariantIds: string[],
  ): Record<string, string> {
    return checkoutItems.reduce<Record<string, string>>((acc, item) => {
      if (checkoutItemProductVariantIds.includes(item.product.variantId)) {
        acc[item.id] = item.product.variantId
      }
      return acc
    }, {})
  }

  async function deleteUnavailableCheckoutItemsDueToOverrideMenu() {
    const overrideCheckoutErrorModalConfig =
      errorModalConfig as InformativeError<ErrorCode.CheckoutItemUnavailableDueToOverrideMenu>

    const { additionalData } = overrideCheckoutErrorModalConfig || {}
    const checkoutItemProductVariantIds =
      additionalData?.checkoutItemProductVariantIds || []

    if (checkoutItemProductVariantIds.length === 0) {
      return
    }

    const companyId = pendingMemberId
      ? clientContextCompanyIdByCompanyMemberIdSelector(
          store.getState(),
          pendingMemberId,
        )
      : null

    const checkoutItemIds = Object.keys(
      getProductVariantIdByItemIds(checkoutItemProductVariantIds),
    )

    try {
      await dispatch(
        deleteCheckoutItems({
          checkoutItemIds,
          companyId,
        }),
      ).unwrap()

      if (pendingMemberId) {
        // Complete the profile switch after successfully clearing unavailable cart items
        localStorageUtils.setItem<string>('companyProfileId', pendingMemberId)
        dispatch(clientContextSetCurrentCompanyUserId(pendingMemberId))
        dispatch(
          setAuthProfileSelectionModalType(ProfileSelectionModalType.None),
        )
        dispatch(setPendingCompanyProfileSwitchMemberId(null))
      }
    } catch (error) {
      noop()
    }
  }

  function handleRequestClose() {
    switch (errorModalConfig?.errorModalType) {
      case ErrorModalType.ProductOutOfStock:
        dispatch(setProductDetailsModalVisible(false))
        break
      case ErrorModalType.CheckoutPaymentError:
        storedPaymentCorrelationId.refresh()
        dispatch(checkoutRetryAdyenPayment())
        break
      case ErrorModalType.CheckPaymentError:
        storedPaymentCorrelationId.refresh()
        dispatch(checkRetryAdyenPayment())
        break
      case ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu:
        dispatch(setPendingCompanyProfileSwitchMemberId(null))
        break
      default:
    }

    dispatch(appSetIsErrorModalVisible(false))
  }

  async function handlePrimaryAction() {
    switch (errorModalConfig?.errorModalType) {
      case ErrorModalType.CheckoutBoxPickupUnAvailableProducts:
        dispatch(checkoutSetIsVisibleBoxPickupScheduleOrderModal(true))
        break
      case ErrorModalType.CheckoutUnavailableOrderTime: {
        const orderTimeErrorModalConfig =
          errorModalConfig as InformativeError<ErrorCode.CheckoutUnavailableOrderTime>
        const isPreOrderCheckout =
          !!orderTimeErrorModalConfig?.additionalData?.isPreOrderCheckout

        if (isPreOrderCheckout) {
          dispatch(setPreOrderTimeChangeModalVisible(true))
        } else {
          dispatch(
            outletSetOrderConfigureFilterModal(
              OrderConfigureFilterMode.OrderTime,
            ),
          )
        }

        break
      }
      case ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu:
        await deleteUnavailableCheckoutItemsDueToOverrideMenu()
        dispatch(setProductDetailsModalVisible(false))
        break
      default:
    }

    dispatch(appSetIsErrorModalVisible(false))
  }

  switch (errorModalConfig?.errorModalType) {
    case ErrorModalType.CheckoutItemPriceChanged:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
        >
          <BodyText className={styles.buttonText}>{t('okay')}</BodyText>
        </Button>
      )
    case ErrorModalType.ProductOutOfStock:
    case ErrorModalType.OutletNotAvailableForOrders:
    case ErrorModalType.CheckoutFetchError:
    case ErrorModalType.CheckoutAddTipError:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
        >
          <BodyText className={styles.buttonText}>{t('close')}</BodyText>
        </Button>
      )
    case ErrorModalType.CheckoutBoxPickupUnAvailableProducts:
      return (
        <>
          <Button
            size="large"
            variant="secondary"
            className={styles.button}
            onClick={handleRequestClose}
          >
            <BodyText className={clsx(styles.buttonText, styles.secondary)}>
              {t('retry')}
            </BodyText>
          </Button>
          <Button
            size="large"
            className={styles.button}
            onClick={handlePrimaryAction}
          >
            <BodyText className={styles.buttonText}>
              {t('boxPickup.changeTimeSlot')}
            </BodyText>
          </Button>
        </>
      )
    case ErrorModalType.CheckoutUnavailableOrderTime:
      return (
        <>
          <Button
            size="large"
            variant="secondary"
            className={styles.button}
            onClick={handleRequestClose}
          >
            <BodyText className={clsx(styles.buttonText, styles.secondary)}>
              {t('retry')}
            </BodyText>
          </Button>
          <Button
            size="large"
            className={styles.button}
            onClick={handlePrimaryAction}
          >
            <BodyText className={styles.buttonText}>
              {t('checkout.changeOrderTime')}
            </BodyText>
          </Button>
        </>
      )
    case ErrorModalType.CheckoutUnavailableProductsDueToOverrideMenu:
      return (
        <>
          <Button
            size="large"
            className={styles.button}
            onClick={handleRequestClose}
          >
            <BodyText className={styles.buttonText}>{t('retry')}</BodyText>
          </Button>
          <Button
            size="large"
            className={styles.button}
            onClick={handlePrimaryAction}
          >
            <BodyText className={styles.buttonText}>
              {t('errors.unAvailableItemDueToOverrideMenu.removeAll')}
            </BodyText>
          </Button>
        </>
      )
    default:
      return (
        <Button
          size="large"
          className={styles.button}
          onClick={handleRequestClose}
        >
          <BodyText className={styles.buttonText}>{t('retry')}</BodyText>
        </Button>
      )
  }
}
